import React, { useEffect } from "react"
import { useState } from "react"
import { Shield } from "lucide-react"
import { Button } from "src/components/ui/button"
import { Skeleton } from "src/components/ui/skeleton"
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "src/components/ui/table"
import { useAuth } from "src/contexts/AuthContext"
import { NoAccess } from "src/components/shared/NoAccess"
import { useRoles } from "src/features/members/hooks/useMembers"
import { RolePermissionsDrawer } from "src/features/members/components/RolePermissionsDrawer"

export default function RolesPage() {
  const { currentMembership } = useAuth()
  const roleSlug = currentMembership?.role?.slug
  const isAdmin = ["org_admin", "site_head", "team_lead"].includes(roleSlug)

  const { roles, loading, fetch } = useRoles()
  const [editing, setEditing] = useState(null)

  useEffect(() => { if (isAdmin) fetch() }, [fetch, isAdmin])

  if (!isAdmin) {
    return <NoAccess message="Only organization admins can manage roles and permissions." />
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold text-slate-900">Roles &amp; Permissions</h2>
        <p className="text-[13px] text-slate-500">
          Configure what each role can do. Privileged roles are managed by Clozdl and aren&apos;t shown here.
        </p>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="w-[50px]">#</TableHead>
              <TableHead className="min-w-[240px]">Role</TableHead>
              <TableHead className="w-[140px]">Members</TableHead>
              <TableHead className="w-[100px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && roles.length === 0 ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {[40, 200, 80, 60].map((w, j) => (
                    <TableCell key={j}><Skeleton className="h-4 rounded" style={{ width: w }} /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              roles.map((role, idx) => (
                <TableRow key={role.id}>
                  <TableCell className="text-xs text-slate-400">{idx + 1}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2.5">
                      <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#ecebff]">
                        <Shield className="h-3.5 w-3.5 text-[#4f39f6]" />
                      </span>
                      <span className="text-[13px] font-medium text-slate-900">{role.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-[13px] text-slate-600">
                    {role.member_count} member{role.member_count !== 1 ? "s" : ""}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="outline" size="sm" onClick={() => setEditing(role)}>
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <RolePermissionsDrawer
        open={!!editing}
        role={editing}
        onClose={() => setEditing(null)}
        onSuccess={fetch}
      />
    </div>
  )
}
